import React from 'react';
import CategoryCalculator from '../components/CategoryCalculator';
import { MTB_CATEGORIES } from '../utils/categoryUtils';

const CategoryGuide: React.FC = () => {
  // Group categories by type
  const nationalCategories = MTB_CATEGORIES.filter(cat => 
    ['NG', 'NB', 'SG', 'SB', 'JG', 'JB', 'YW', 'YM'].includes(cat.code)
  );
  
  const uciCategories = MTB_CATEGORIES.filter(cat => 
    ['JW', 'JM', 'UW', 'UM', 'SW', 'SM', 'EW', 'EM'].includes(cat.code)
  );
  
  const mastersCategories = MTB_CATEGORIES.filter(cat => 
    ['SX', 'SV', 'VW', 'VM', 'MW', 'MM', 'GW', 'GM'].includes(cat.code)
  );

  return (
    <div className="bg-gray-50 font-sans text-dark min-h-screen pb-20">
      {/* Header */}
      <header className="app-header sticky top-0 z-50">
        <div className="flex items-center justify-center">
          <h1 className="app-header-title">MTB Categories</h1>
        </div>
      </header>

      {/* Main Content */}
      <main className="content-container">
        <div className="bg-white rounded-lg shadow-md p-6 mb-6">
          <h2 className="text-xl font-bold mb-2">MTB Racing Categories</h2>
          <p className="text-gray-600 mb-4">
            Mountain bike racing categories are determined by the age a rider will turn during the current calendar year,
            regardless of when their actual birthday falls.
          </p>
          
          <div className="overflow-x-auto">
            <table className="min-w-full border-collapse">
              <thead>
                <tr className="bg-gray-100">
                  <th colSpan={3} className="px-4 py-2 text-left border">National Entry Level</th>
                </tr>
                <tr className="bg-gray-50">
                  <th className="px-4 py-2 text-left border">Code</th>
                  <th className="px-4 py-2 text-left border">Category</th>
                  <th className="px-4 py-2 text-left border">Age (yrs)</th>
                </tr>
              </thead>
              <tbody>
                {nationalCategories.map((cat, index) => (
                  <tr key={index} className={index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                    <td className="px-4 py-2 border">{cat.code}</td>
                    <td className="px-4 py-2 border">{cat.category}</td>
                    <td className="px-4 py-2 border">{cat.minAge}-{cat.maxAge}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          
          <div className="overflow-x-auto mt-6">
            <table className="min-w-full border-collapse">
              <thead>
                <tr className="bg-gray-100">
                  <th colSpan={3} className="px-4 py-2 text-left border">UCI Categories</th>
                </tr>
                <tr className="bg-gray-50">
                  <th className="px-4 py-2 text-left border">Code</th>
                  <th className="px-4 py-2 text-left border">Category</th>
                  <th className="px-4 py-2 text-left border">Age (yrs)</th>
                </tr>
              </thead>
              <tbody>
                {uciCategories.map((cat, index) => (
                  <tr key={index} className={index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                    <td className="px-4 py-2 border">{cat.code}</td>
                    <td className="px-4 py-2 border">{cat.category}</td>
                    <td className="px-4 py-2 border">
                      {cat.maxAge === 200 ? `${cat.minAge}+` : `${cat.minAge}-${cat.maxAge}`}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          
          <div className="overflow-x-auto mt-6">
            <table className="min-w-full border-collapse">
              <thead>
                <tr className="bg-gray-100">
                  <th colSpan={3} className="px-4 py-2 text-left border">UCI Masters Categories</th>
                </tr>
                <tr className="bg-gray-50">
                  <th className="px-4 py-2 text-left border">Code</th>
                  <th className="px-4 py-2 text-left border">Category</th>
                  <th className="px-4 py-2 text-left border">Age (yrs)</th>
                </tr>
              </thead>
              <tbody>
                {mastersCategories.map((cat, index) => (
                  <tr key={index} className={index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                    <td className="px-4 py-2 border">{cat.code}</td>
                    <td className="px-4 py-2 border">{cat.category}</td>
                    <td className="px-4 py-2 border">
                      {cat.maxAge === 200 ? `${cat.minAge}+` : `${cat.minAge}-${cat.maxAge}`}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
        
        {/* Category Calculator */}
        <CategoryCalculator />
        
        <div className="bg-white rounded-lg shadow-md p-6 mt-6">
          <h2 className="text-xl font-bold mb-2">Category Rules Explained</h2>
          <div className="space-y-4">
            <p>
              <strong>Age Calculation Rule:</strong> A rider's category is determined by the age they will turn during the current calendar year, 
              regardless of when their actual birthday falls.
            </p>
            <p>
              <strong>Example 1:</strong> If a rider was born on December 15, 2017, and it's currently March 2025, 
              they will be placed in the Nipper category because they will turn 8 in 2025, even though they are only 7 years old in March.
            </p>
            <p>
              <strong>Example 2:</strong> If a rider was born on January 2, 2007, and it's December 2025, 
              they will be placed in the Junior category for the entire 2025 season because they turned 18 in 2025, 
              even though they're almost 19 by December.
            </p>
            <p>
              <strong>Elite Categories:</strong> Riders aged 23 and above may choose to compete in either their age-specific category 
              (Senior, Sub Vet, etc.) or in the Elite category, depending on their competitive level.
            </p>
          </div>
        </div>
      </main>
    </div>
  );
};

export default CategoryGuide;
